import { useEffect, useState } from 'react';
import '../Assets/CSS/Legend.css';

/**
 * What the site's controls are, behind one word.
 *
 * It is open state and nothing else — nothing about the scene is drawn in it, which is why it is a
 * real `<button>` in the app's own markup rather than a mesh in the HUD: the keyboard reaches it,
 * the text is the browser's to render crisply at any size, and none of the pointer machinery in
 * `NavBar` has to be taught about it.
 *
 * It does carry two sets of lines, though, because the page has two places to be: the lobby, where
 * the gate is and the ship is walked, and the solar system, where every control the rest of the
 * chrome drives belongs. `setLegend` below is how the scene asks for one of them — the scene knows
 * when it has moved, because moving is what it does.
 *
 * Each place carries the two in two shapes, because a mouse and a finger want different sentences
 * and not the same sentence with a word swapped. The pointer's is a `what` and a `how` — a table to
 * be scanned, since a mouse has a wheel, a second button and a keyboard behind it and the reader is
 * looking up one control. The touchscreen's is prose, in the order the page is used, since a finger
 * has one gesture and the list is short enough to read straight through.
 *
 * Both are in the markup and the stylesheet picks between them, so the breakpoint stays in the
 * Legend's own media query rather than in a listener watching the window for a change of width.
 *
 * The place it sits is not taste: the coin is at a fixed point of the frustum and the arithmetic is
 * in `Legend.css`, beside the two numbers.
 */
const LINES = {
    lobby: {
        pointer: [
            ['Move', 'W A S D'],
            ['Look', 'move the mouse, or drag if that does nothing'],
            ['The way out', 'look round the ship, and click whatever the crosshair finds'],
            ['This list', 'click Legend to close it'],
        ],
        touch: [
            'Swipe to look around.',
            'Find the Exit button and tap it to leave the ship.',
            'Tap "Legend" to close this.',
        ],
    },
    solar: {
        pointer: [
            ['Logo', 'click to open the menu'],
            ['Planets', 'click one to view its category'],
            ['Double-click or Esc', 'go back'],
            ['Images', 'mouse wheel, or drag left and right, to turn the ring'],
            ['Solar system', 'left-drag to look around, right-drag to move sideways'],
        ],
        touch: [
            'Tap the logo above to open a category menu.',
            'Tap a planet (or a button if menu is open) to view the category.',
            'Double-tap or tap "Back" to go back.',
            'Swipe right and left to turn the image ring.',
            'Tap to view full image (2 finger pinch to zoom the image).',
            'Swipe single finger to look around space.',
            'Pinch 2 fingers to move forward or backwards.',
            'Swipe 2 fingers to move left and right in space.',
        ],
    },
};

/**
 * The scene's way in, and the only thing that writes it. `LobbyContainer` calls it twice: `null`
 * when the doors are opened, which closes the list, and `'solar'` when a move lands, which opens it
 * on the second set.
 *
 * It is a function the mounted component leaves behind — rather than a store outside React, or a
 * class toggled on a node the way the Skip and the blackout are — because *both* of the things that
 * change are React state. A store would be a second copy of them that can disagree with what is on
 * screen, and a class can carry only one bit: it can say whether the list is drawn, but it cannot
 * say which of the two lists it is.
 *
 * Calls before the component has mounted are dropped, and that is all that is owed. The page opens
 * in the lobby, so the lobby's lines are this component's own initial state and the scene never has
 * to ask for them — its first call is the one that ends them.
 */
let apply = null;

export function setLegend(view) {
    if (apply) apply(view);
}

export default function Legend() {
    const [view, setView] = useState('lobby');
    const [open, setOpen] = useState(true);

    useEffect(() => {
        apply = next => {
            if (next) setView(next);
            setOpen(Boolean(next));
        };

        return () => { apply = null; };
    }, []);

    return (
        <div className="legend">
            <button
                className="legend__toggle"
                type="button"
                aria-expanded={open}
                onClick={() => setOpen(shown => !shown)}
            >
                Legend
            </button>

            {open && (
                <>
                    <dl className="legend__list legend__pointer">
                        {LINES[view].pointer.map(([what, how]) => (
                            <div className="legend__row" key={what}>
                                <dt>{what}</dt>
                                <dd>{how}</dd>
                            </div>
                        ))}
                    </dl>

                    <ul className="legend__list legend__touch">
                        {LINES[view].touch.map(line => (
                            <li key={line}>{line}</li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}
